import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/cn';

const statusPillVariants = cva(
  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide whitespace-nowrap',
  {
    variants: {
      status: {
        paid: 'bg-status-paid-bg text-status-paid border-status-paid-border',
        open: 'bg-status-open-bg text-status-open border-status-open-border',
        late: 'bg-status-late-bg text-status-late border-status-late-border',
        draft: 'bg-status-draft-bg text-status-draft border-status-draft-border',
        partial: 'bg-status-partial-bg text-status-partial border-status-partial-border',
        void: 'bg-status-void-bg text-status-void border-status-void-border',
        success: 'bg-status-paid-bg text-status-paid border-status-paid-border',
        warning: 'bg-status-partial-bg text-status-partial border-status-partial-border',
        error: 'bg-status-late-bg text-status-late border-status-late-border',
        neutral: 'bg-status-draft-bg text-status-draft border-status-draft-border',
      },
    },
    defaultVariants: {
      status: 'neutral',
    },
  },
);

export type StatusPillStatus = NonNullable<VariantProps<typeof statusPillVariants>['status']>;

export interface StatusPillProps extends VariantProps<typeof statusPillVariants> {
  /** Defaults to the status name uppercased. */
  label?: string;
  /** Show the colored dot (default true). */
  dot?: boolean;
  className?: string;
}

/** Maps an invoice state (+ derived LATE) to a pill status. */
export function invoiceStatusToPill(state: string, isLate?: boolean): StatusPillStatus {
  if (isLate) return 'late';
  const s = state.toLowerCase();
  if (s === 'paid' || s === 'open' || s === 'draft' || s === 'partial' || s === 'void' || s === 'late') {
    return s as StatusPillStatus;
  }
  return 'neutral';
}

export function StatusPill({ status, label, dot = true, className }: StatusPillProps) {
  return (
    <span className={cn(statusPillVariants({ status }), className)}>
      {dot && <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden />}
      {label ?? (status ?? 'neutral').toUpperCase()}
    </span>
  );
}
